import { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { readString } from "react-papaparse";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import Modal, { setSelectedModal } from "../../Modal";
import { MODALS, MODAL_SIZE } from "../modals.constants";
import { addFolderIcon, deleteIcon } from "../../../../assets/icons";
import { setSelectedDrawer } from "../../Drawer";
import {
  fetchCollections,
  importCollection,
  selectSelectedDataModelId,
} from "../../../../pages/DataModels";
import { EMPTY_STRING, ONE, ZERO } from "../../../../constants";
import ProgressBar from "../../ProgressBar/ProgressBar";

const REJECTED = "rejected";
const IN_PROGRESS = "inProgress";
const COMPLETED = "completed";
const XLSXExtenxion = "xlsx";
const XLSExtention = "xls";
const TIME_OUT = 5000;
const PROGRESS_TIMOUT = 1000;

const FileImportProgressModal = () => {
  const dataModelId = useSelector(selectSelectedDataModelId);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState();
  const [progress, setProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState();
  const [progressStatus, setProgressStatus] = useState();

  useEffect(() => {
    if (progress === 100) {
      setProgressStatus(COMPLETED);
      dispatch(setSelectedModal());
    }
  }, [progress, dispatch]);

  const getFileExtension = (filename) => {
    const parts = filename.split(".");
    if (parts.length > ONE) return parts[parts.length - ONE];
    else return EMPTY_STRING;
  };

  const handleImport = () => {
    fileInputRef.current.click();
  };

  const increaseProgress = useCallback(() => {
    setProgress((prevProgress) =>
      prevProgress < 100 ? prevProgress + 10 : 100
    );
  }, [setProgress]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      progressStatus && progressStatus !== REJECTED && increaseProgress();
    }, PROGRESS_TIMOUT);

    return () => clearInterval(intervalId);
  }, [progressStatus, increaseProgress]);

  const handleCSVImport = ({ file, fileInput, fileName, extension }) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      const csvData = event.target.result;

      try {
        const parsedData = await new Promise((resolve, reject) => {
          readString(csvData, {
            header: true,
            dynamicTyping: true,
            complete: (parsedData) => resolve(parsedData),
            error: (error) => reject(error),
          });
        });

        const inputFields = Object.keys(parsedData.data[ZERO]);
        dispatch(
          importCollection({
            dataModelId,
            updatedTableDetails: {
              tableName: fileName,
              inputJson: parsedData.data,
              tableFields: inputFields,
              extension,
            },
          })
        ).then((res) => {
          const status = res.meta.requestStatus;
          if (status !== REJECTED) {
            dispatch(
              fetchCollections({
                id: dataModelId,
              })
            );
            setProgressStatus(status);
          } else setProgressStatus(null); 
        });

        fileInput.value = null;
      } catch (error) {
        fileInput.value = null;
      }
    };
    reader.readAsText(file);
  };

  const handleImportXLSX = ({ file, extension }) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: "binary" });

      const importPromises = workbook.SheetNames.map(async (sheetName) => {
        const sheet = workbook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const filteredData = parsedData.filter((each) => !isEmpty(each));

        const inputFields = Object.values(filteredData[0]);

        return dispatch(
          importCollection({
            dataModelId,
            updatedTableDetails: {
              tableName: sheetName,
              inputJson: filteredData.slice(1),
              tableFields: inputFields,
              extension,
            },
          })
        ).then((res) =>  {
          const status = res.meta.requestStatus;
          if (status === REJECTED) 
            setProgressStatus(null);
          else setProgressStatus(status);
        })
      });

      Promise.all(importPromises).finally(() => {
        setTimeout(() => {
          dispatch(
            fetchCollections({
              id: dataModelId,
            })
          );
        }, TIME_OUT);
      });
    };
    reader.readAsBinaryString(file);
  };

  const handleImportCollection = async (event) => {
    const fileInput = event.target;
    const file = fileInput.files[ZERO];
    const fileName = file.name;
    const extension = getFileExtension(fileName);
    setSelectedFile(fileName);
    if (!file) return;
    dispatch(setSelectedDrawer());
    dispatch(setSelectedModal(MODALS.FILE_IMPORT_MODAL));
    const fileToBeImported = { file, fileInput, fileName, extension };
    setUploadedFile(fileToBeImported);
  };

  const handleUploadFile = (fileDetails) => {
    const { file, fileInput, fileName, extension } = fileDetails;
    setProgress(ZERO);
    setProgressStatus(IN_PROGRESS);

    if (extension === XLSXExtenxion || extension === XLSExtention)
      handleImportXLSX({ file, extension });
    else handleCSVImport({ file, fileInput, fileName, extension });
  };

  const handleClose = () => {
    dispatch(setSelectedModal());
    setSelectedFile();
    setProgress(ZERO);
  };
  const footer = (
    <div className="p-6 border-t border-gray-700 flex align-center justify-end">
      <button
        type="button"
        className="btn btn-primary w-full xs:w-auto disabled:cursor-not-allowed"
        onClick={() => handleUploadFile(uploadedFile)}
        disabled={ !selectedFile || (progressStatus && !progressStatus.includes([REJECTED]))}
      >
        Upload File
      </button>
    </div>
  );
  return (
    <Modal
      title="Create Collections by Importing files"
      size={MODAL_SIZE.MD}
      handleClose={handleClose}
      footer={footer}
    >
      <div className="overflow-auto no-scrollbar">
        <div className="flex-grow m-auto p-4">
          <div
            className="border border-gray-300 border-dashed rounded-md p-7 grid place-content-center cursor-pointer lg:h-80 md:h-60 "
            onClick={handleImport}
          >
            <span>
              <div className="text-center">
                <div className="icon mb-1">
                  <img
                    src={addFolderIcon}
                    className="mx-auto w-30 h-20"
                    alt="add-icon"
                  />
                </div>
                <div className="mb-1">
                  <div className="text-indigo-200 hover:opacity-90 hover:underline text-lg inline">
                    Drop your File here or Browse
                  </div>
                </div>
              </div>
            </span>
            <input
              type="file"
              className="hidden"
              accept=".csv,.xlsx,.xls"
              onChange={handleImportCollection}
              ref={fileInputRef}
            />
          </div>
        </div>
        <div>
          {selectedFile && (
            <div className="px-4 pb-2">
              <div className="flex flex-col items-start sm:items-center border-dashed border border-gray-500 p-2 gap-2 rounded">
                <div className="flex justify-between items-center w-full">
                  <div className="text-gray-50 flex justify-center text-xs sm:text-sm md:text-base">
                    {selectedFile}
                  </div>
                  <div className="ml-auto">
                    <button
                      className="rounded bg-gray-900 w-6 h-6 m-1 flex justify-center items-center flex-col disabled:cursor-not-allowed disabled:bg-gray-600"
                      onClick={() => {
                        setSelectedFile();
                        setProgress(0);
                      }}
                      disabled={
                        progressStatus && !progressStatus?.includes([REJECTED])
                      }
                    >
                      <img
                        src={deleteIcon}
                        alt="delete-icon"
                        className="w-5 h-4"
                      />
                    </button>
                  </div>
                </div>
                <div className="w-full">
                  {progressStatus && (
                    <div className="w-full">
                      <ProgressBar
                        barType="line"
                        progress={progress}
                        progressStatus={progressStatus}
                      ></ProgressBar>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FileImportProgressModal;
